import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalIcon, Plus, Check, X, Trash2, Clock, CheckCircle2,
  XCircle, User as UserIcon, Loader2, Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const STATUS_INFO = {
  pending:  { label: "Wartet auf Genehmigung", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  approved: { label: "Genehmigt", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Abgelehnt", color: "bg-rose-100 text-rose-800 border-rose-300", icon: XCircle },
};

// Farbe pro Mitarbeiter (deterministisch)
const USER_COLORS = {
  "u-markus-001":   "#8B6914",
  "u-linda-007":    "#a855f7",
  "u-angelina-002": "#0ea5e9",
  "u-annika-003":   "#10b981",
  "u-julia-004":    "#f59e0b",
  "u-jean-006":     "#ef4444",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const Urlaubsplanung = ({ currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [jahr, setJahr] = useState(String(new Date().getFullYear() < 2026 ? 2026 : new Date().getFullYear()));
  const [antraege, setAntraege] = useState([]);
  const [saldo, setSaldo] = useState({ mitarbeiter: [] });
  const [feiertage, setFeiertage] = useState([]); // {datum, name, wochentag, halbtag?}
  const [neuOpen, setNeuOpen] = useState(false);
  const [form, setForm] = useState({ start_datum: "", ende_datum: "", kommentar: "" });
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("liste"); // "liste" | "kalender"
  const [kontingentOpen, setKontingentOpen] = useState(false);
  const [kontingentTarget, setKontingentTarget] = useState(null); // saldo-Eintrag
  const [kontingentForm, setKontingentForm] = useState({ jahreskontingent: 30, uebertrag_vorjahr: 0, notiz: "" });
  const [kontingentSaving, setKontingentSaving] = useState(false);

  const istGenehmiger = currentUser?.id === "u-markus-001" || currentUser?.role === "gf";

  // Zahlen deutsch formatieren: 4.5 -> "4,5" / 30 -> "30"
  const fmtNum = (n) => {
    if (n === null || n === undefined) return "0";
    const num = Number(n);
    return num.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, f] = await Promise.all([
        axios.get(`${API}/urlaub/antraege?jahr=${jahr}`),
        axios.get(`${API}/urlaub/saldo?jahr=${jahr}`),
        axios.get(`${API}/urlaub/feiertage?jahr=${jahr}`),
      ]);
      setAntraege(a.data || []);
      setSaldo(s.data || { mitarbeiter: [] });
      setFeiertage(f.data?.feiertage || []);
    } catch (e) {
      toast.error("Konnte Urlaubsdaten nicht laden");
    } finally {
      setLoading(false);
    }
  }, [jahr]);

  useEffect(() => { load(); }, [load]);

  const stellen = async () => {
    if (!form.start_datum || !form.ende_datum) {
      toast.error("Start- und Endedatum sind Pflicht");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/urlaub/antrag`, form);
      toast.success("Urlaubsantrag gestellt — wartet auf Genehmigung durch Linda Lüppe");
      setNeuOpen(false);
      setForm({ start_datum: "", ende_datum: "", kommentar: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Antrag fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const genehmigen = async (a) => {
    const k = window.prompt(`Genehmigen: ${a.user_name} ${formatDate(a.start_datum)}–${formatDate(a.ende_datum)} (${a.tage} Tage). Kommentar (optional):`, "");
    if (k === null) return;
    try {
      await axios.put(`${API}/urlaub/antrag/${a.id}/genehmigen`, { kommentar_genehmiger: k });
      toast.success("Genehmigt ✓");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Genehmigung fehlgeschlagen");
    }
  };

  const ablehnen = async (a) => {
    const k = window.prompt(`Ablehnen: ${a.user_name} ${formatDate(a.start_datum)}–${formatDate(a.ende_datum)}. Begründung:`, "");
    if (k === null) return;
    try {
      await axios.put(`${API}/urlaub/antrag/${a.id}/ablehnen`, { kommentar_genehmiger: k });
      toast.success("Abgelehnt");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen");
    }
  };

  const loeschen = async (a) => {
    if (!window.confirm(`Antrag wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/urlaub/antrag/${a.id}`);
      toast.success("Antrag gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const speicherKontingent = async () => {
    if (!kontingentTarget) return;
    setKontingentSaving(true);
    try {
      await axios.put(
        `${API}/urlaub/kontingent/${kontingentTarget.user_id}?jahr=${jahr}`,
        {
          jahreskontingent: Number(kontingentForm.jahreskontingent),
          uebertrag_vorjahr: Number(kontingentForm.uebertrag_vorjahr),
          notiz: kontingentForm.notiz || "",
        }
      );
      toast.success(`Kontingent für ${kontingentTarget.name} gespeichert`);
      setKontingentOpen(false);
      setKontingentTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setKontingentSaving(false);
    }
  };

  // Feiertage als Map: "YYYY-MM-DD" -> {name, halbtag?}
  const feiertagMap = useMemo(() => {
    const m = {};
    for (const f of feiertage) {
      m[f.datum] = f;
    }
    return m;
  }, [feiertage]);

  // Kalender-Matrix: ein Tag pro Spalte, je Mitarbeiter eine Zeile
  const kalenderDaten = useMemo(() => {
    const startY = parseInt(jahr, 10);
    const monate = [];
    for (let m = 0; m < 12; m++) {
      const days = new Date(startY, m + 1, 0).getDate();
      monate.push({ monatIdx: m, days });
    }
    // Map: user_id → { "YYYY-MM-DD": antrag }
    const mapping = {};
    for (const a of antraege) {
      if (a.status === "rejected") continue;
      const s = new Date(a.start_datum + "T00:00:00");
      const e = new Date(a.ende_datum + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        mapping[a.user_id] = mapping[a.user_id] || {};
        // approved überschreibt pending in der Anzeige
        if (!mapping[a.user_id][key] || mapping[a.user_id][key].status !== "approved") {
          mapping[a.user_id][key] = a;
        }
      }
    }
    return { monate, mapping };
  }, [antraege, jahr]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#4A463D] flex items-center gap-2">
            <CalIcon size={28} /> Urlaubsplanung
          </h1>
          <p className="text-stone-600 text-sm">
            30 Tage Urlaub pro Kalenderjahr · Genehmigung durch Linda Lüppe · Heiligabend &amp; Silvester zählen je ½ Tag
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border bg-white p-1">
            {["2025", "2026", "2027"].map(j => (
              <button key={j}
                onClick={() => setJahr(j)}
                data-testid={`urlaub-jahr-${j}`}
                className={`px-3 py-1 text-sm rounded-md ${
                  jahr === j ? "bg-[#4A463D] text-white" : "text-stone-700 hover:bg-stone-100"
                }`}
              >{j}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border bg-white p-1">
            {[["liste","Liste"],["kalender","Kalender"]].map(([v,l]) => (
              <button key={v}
                onClick={() => setView(v)}
                data-testid={`urlaub-view-${v}`}
                className={`px-3 py-1 text-sm rounded-md ${
                  view === v ? "bg-[#4A463D] text-white" : "text-stone-700 hover:bg-stone-100"
                }`}
              >{l}</button>
            ))}
          </div>
          <Button
            onClick={() => setNeuOpen(true)}
            className="bg-[#4A463D] hover:bg-[#3A362D] text-white"
            data-testid="urlaub-neu-btn"
          >
            <Plus size={16} className="mr-1" /> Urlaub beantragen
          </Button>
        </div>
      </div>

      {/* Saldo-Übersicht */}
      <div>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Saldo {jahr}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {saldo.mitarbeiter?.map(m => {
            const gesamt = m.gesamt_verfuegbar ?? m.jahreskontingent;
            const proz = gesamt > 0 ? Math.min(100, Math.round((m.genehmigt / gesamt) * 100)) : 0;
            const hatUebertrag = (m.uebertrag_vorjahr || 0) !== 0;
            return (
              <div key={m.user_id}
                className="rounded-lg border bg-white p-3 relative group"
                data-testid={`saldo-${m.user_id}`}
                style={{ borderLeftWidth: 4, borderLeftColor: USER_COLORS[m.user_id] || "#888" }}
              >
                {istGenehmiger && (
                  <button
                    type="button"
                    onClick={() => {
                      setKontingentTarget(m);
                      setKontingentForm({
                        jahreskontingent: m.jahreskontingent ?? 30,
                        uebertrag_vorjahr: m.uebertrag_vorjahr ?? 0,
                        notiz: m.notiz || "",
                      });
                      setKontingentOpen(true);
                    }}
                    className="absolute top-2 right-2 h-6 w-6 rounded flex items-center justify-center text-stone-400 hover:text-[#4A463D] hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition"
                    title="Kontingent + Übertrag Vorjahr bearbeiten"
                    data-testid={`saldo-edit-${m.user_id}`}
                  >
                    <Pencil size={12} />
                  </button>
                )}
                <div className="text-xs text-stone-500 truncate pr-6">{m.name}</div>
                <div className="text-lg font-bold text-stone-800">
                  {fmtNum(m.verbleibend)} <span className="text-xs font-normal text-stone-500">/ {fmtNum(gesamt)} Tage</span>
                </div>
                <div className="mt-1 h-1.5 bg-stone-100 rounded overflow-hidden">
                  <div className="h-full" style={{ width: `${proz}%`, backgroundColor: USER_COLORS[m.user_id] || "#888" }} />
                </div>
                <div className="text-[10px] text-stone-500 mt-1 leading-tight">
                  <div>✓ {fmtNum(m.genehmigt)} genehmigt{m.offen_pending > 0 && <> · {fmtNum(m.offen_pending)} offen</>}</div>
                  {hatUebertrag && (
                    <div className="text-[#8B6914] font-medium">
                      Übertrag Vorjahr: {m.uebertrag_vorjahr > 0 ? "+" : ""}{fmtNum(m.uebertrag_vorjahr)} T.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin" /></div>
      )}

      {/* LISTE */}
      {!loading && view === "liste" && (
        <div className="rounded-lg border overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="w-44">Mitarbeiter</TableHead>
                <TableHead className="w-28">Von</TableHead>
                <TableHead className="w-28">Bis</TableHead>
                <TableHead className="w-16 text-center">Tage</TableHead>
                <TableHead>Kommentar</TableHead>
                <TableHead className="w-44">Status</TableHead>
                <TableHead className="w-56 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {antraege.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-stone-500 py-6">
                  Noch keine Anträge für {jahr}.
                </TableCell></TableRow>
              )}
              {antraege.map(a => {
                const Info = STATUS_INFO[a.status] || STATUS_INFO.pending;
                const Icon = Info.icon;
                const eigener = a.user_id === currentUser?.id;
                return (
                  <TableRow key={a.id} data-testid={`urlaub-zeile-${a.id}`}>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-6 rounded" style={{ backgroundColor: USER_COLORS[a.user_id] || "#888" }} />
                        {a.user_name}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(a.start_datum)}</TableCell>
                    <TableCell className="text-sm">{formatDate(a.ende_datum)}</TableCell>
                    <TableCell className="text-sm text-center font-semibold">{fmtNum(a.tage)}</TableCell>
                    <TableCell className="text-xs text-stone-600">
                      {a.kommentar}
                      {a.kommentar_genehmiger && (
                        <div className="mt-1 text-[10px] italic text-stone-500">
                          {a.genehmigt_von}: „{a.kommentar_genehmiger}"
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`gap-1 ${Info.color}`}>
                        <Icon size={12} /> {Info.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {a.status === "pending" && istGenehmiger && (
                          <>
                            <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
                              onClick={() => genehmigen(a)} data-testid={`genehmigen-${a.id}`}>
                              <Check size={14} className="mr-1" /> Genehmigen
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-rose-700 border-rose-300 hover:bg-rose-50"
                              onClick={() => ablehnen(a)} data-testid={`ablehnen-${a.id}`}>
                              <X size={14} className="mr-1" /> Ablehnen
                            </Button>
                          </>
                        )}
                        {(eigener && a.status === "pending") || istGenehmiger ? (
                          <Button size="sm" variant="ghost" className="h-8 text-stone-500 hover:text-rose-700"
                            onClick={() => loeschen(a)} data-testid={`loeschen-${a.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* KALENDER (kompakt: 12 Monatsblöcke, je Mitarbeiter eine Reihe) */}
      {!loading && view === "kalender" && (
        <div className="space-y-4">
          {kalenderDaten.monate.map(({ monatIdx, days }) => {
            const monatName = new Date(parseInt(jahr,10), monatIdx, 1).toLocaleString("de-DE", { month: "long" });
            return (
              <div key={monatIdx} className="rounded-lg border bg-white overflow-x-auto">
                <div className="px-3 py-1 bg-stone-100 border-b text-sm font-semibold text-[#4A463D]">{monatName} {jahr}</div>
                <table className="w-full text-[10px]">
                  <thead>
                    <tr>
                      <th className="w-32 px-2 py-1 text-left bg-stone-50 sticky left-0 z-10">Mitarbeiter</th>
                      {Array.from({ length: days }, (_, i) => i + 1).map(d => {
                        const dt = new Date(parseInt(jahr,10), monatIdx, d);
                        const we = dt.getDay() === 0 || dt.getDay() === 6;
                        const key = `${jahr}-${String(monatIdx+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                        const fx = feiertagMap[key];
                        return (
                          <th key={d}
                            className={`w-6 text-center font-normal py-1 ${we ? "bg-stone-100 text-stone-400" : fx ? "text-[#1e40af]" : "bg-stone-50"}`}
                            style={fx && !fx.halbtag ? { backgroundColor: "#dbeafe" } : fx && fx.halbtag ? { backgroundColor: "#fef3c7" } : undefined}
                            title={fx ? `${fx.name} (${fx.wochentag})` : ""}
                          >{d}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {saldo.mitarbeiter?.map(m => (
                      <tr key={m.user_id} className="border-t">
                        <td className="px-2 py-1 sticky left-0 bg-white border-r truncate"
                          style={{ borderLeftWidth: 3, borderLeftColor: USER_COLORS[m.user_id] }}>
                          {m.name.split(" ")[0]}
                        </td>
                        {Array.from({ length: days }, (_, i) => i + 1).map(d => {
                          const key = `${jahr}-${String(monatIdx+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                          const dt = new Date(parseInt(jahr,10), monatIdx, d);
                          const we = dt.getDay() === 0 || dt.getDay() === 6;
                          const fx = feiertagMap[key];
                          const ant = kalenderDaten.mapping[m.user_id]?.[key];
                          let bg = "";
                          if (ant) {
                            const c = USER_COLORS[m.user_id] || "#888";
                            bg = ant.status === "approved" ? c : "transparent";
                          } else if (fx && !fx.halbtag) {
                            bg = "#dbeafe";  // Feiertag (voll) - hellblau
                          } else if (fx && fx.halbtag) {
                            bg = "#fef3c7";  // 24./31.12. - hellgelb
                          }
                          let title = "";
                          if (ant) title = `${ant.user_name}: ${formatDate(ant.start_datum)}–${formatDate(ant.ende_datum)} (${ant.status})`;
                          else if (fx) title = `${fx.name} (${fx.wochentag})`;
                          return (
                            <td key={d}
                              className={`w-6 h-6 text-center ${we ? "bg-stone-100" : ""}`}
                              style={{
                                backgroundColor: bg,
                                ...(ant && ant.status === "pending"
                                  ? { backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, ${USER_COLORS[m.user_id]} 3px, ${USER_COLORS[m.user_id]} 5px)` }
                                  : {})
                              }}
                              title={title}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
          <div className="text-xs text-stone-500 flex flex-wrap gap-4 items-center pt-1">
            <span><span className="inline-block w-3 h-3 bg-amber-500 align-middle mr-1" /> Volle Farbe = genehmigt</span>
            <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, #f59e0b 3px, #f59e0b 5px)" }} /> Schraffiert = wartet auf Genehmigung</span>
            <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ backgroundColor: "#dbeafe" }} /> NRW-Feiertag (0 Tage)</span>
            <span><span className="inline-block w-3 h-3 align-middle mr-1" style={{ backgroundColor: "#fef3c7" }} /> 24./31.12. (½ Tag)</span>
          </div>
          {feiertage.length > 0 && (
            <div className="mt-4 rounded-lg border bg-white p-3">
              <div className="text-sm font-semibold text-[#4A463D] mb-2">
                NRW-Feiertage {jahr} <span className="text-xs font-normal text-stone-500">— werden nicht vom Urlaub abgezogen</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                {feiertage.map(f => (
                  <div key={f.datum} className="flex items-baseline gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-sm mt-0.5 shrink-0"
                      style={{ backgroundColor: f.halbtag ? "#fef3c7" : "#dbeafe", border: "1px solid #cbd5e1" }}
                    />
                    <span className="tabular-nums text-stone-500">{formatDate(f.datum)}</span>
                    <span className="text-stone-700">{f.name}</span>
                    <span className="text-stone-400 hidden md:inline">· {f.wochentag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Neuer Antrag Dialog */}
      <Dialog open={neuOpen} onOpenChange={setNeuOpen}>
        <DialogContent data-testid="urlaub-neu-modal">
          <DialogHeader>
            <DialogTitle>Urlaub beantragen</DialogTitle>
            <DialogDescription>Antrag geht an Linda Lüppe zur Genehmigung.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start">Von</Label>
                <Input id="start" type="date" value={form.start_datum}
                  onChange={e => setForm({ ...form, start_datum: e.target.value })}
                  data-testid="urlaub-start-input" />
              </div>
              <div>
                <Label htmlFor="ende">Bis</Label>
                <Input id="ende" type="date" value={form.ende_datum}
                  onChange={e => setForm({ ...form, ende_datum: e.target.value })}
                  data-testid="urlaub-ende-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="komm">Kommentar (optional)</Label>
              <Textarea id="komm" rows={3} value={form.kommentar}
                onChange={e => setForm({ ...form, kommentar: e.target.value })}
                placeholder="z.B. Sommerurlaub, Familienurlaub..."
                data-testid="urlaub-kommentar-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNeuOpen(false)}>Abbrechen</Button>
            <Button onClick={stellen} disabled={submitting}
              className="bg-[#4A463D] hover:bg-[#3A362D] text-white"
              data-testid="urlaub-stellen-btn">
              {submitting ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Antrag stellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Kontingent bearbeiten (nur GF) */}
      <Dialog open={kontingentOpen} onOpenChange={setKontingentOpen}>
        <DialogContent data-testid="urlaub-kontingent-modal">
          <DialogHeader>
            <DialogTitle>Kontingent bearbeiten – {kontingentTarget?.name}</DialogTitle>
            <DialogDescription>
              Passe Jahreskontingent und Übertrag aus dem Vorjahr an. Wirkt sich sofort auf den Saldo {jahr} aus.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="k-jahres">Jahreskontingent</Label>
                <Input id="k-jahres" type="number" step="0.5" min="0"
                  value={kontingentForm.jahreskontingent}
                  onChange={e => setKontingentForm({ ...kontingentForm, jahreskontingent: e.target.value })}
                  data-testid="urlaub-kontingent-jahres-input"
                />
                <p className="text-[10px] text-stone-500 mt-1">Standard: 30 Tage</p>
              </div>
              <div>
                <Label htmlFor="k-uebertrag">Übertrag aus Vorjahr</Label>
                <Input id="k-uebertrag" type="number" step="0.5"
                  value={kontingentForm.uebertrag_vorjahr}
                  onChange={e => setKontingentForm({ ...kontingentForm, uebertrag_vorjahr: e.target.value })}
                  data-testid="urlaub-kontingent-uebertrag-input"
                />
                <p className="text-[10px] text-stone-500 mt-1">z.B. 4,5 aus {parseInt(jahr, 10) - 1}</p>
              </div>
            </div>
            <div>
              <Label htmlFor="k-notiz">Notiz (optional)</Label>
              <Textarea id="k-notiz" rows={2}
                value={kontingentForm.notiz}
                onChange={e => setKontingentForm({ ...kontingentForm, notiz: e.target.value })}
                placeholder="z.B. 4,5 Tage aus 2025 lt. Absprache vom 15.01.2026"
                data-testid="urlaub-kontingent-notiz-input"
              />
            </div>
            <div className="rounded-md bg-stone-50 border p-3 text-xs">
              <div className="font-semibold text-stone-700 mb-1">Vorschau</div>
              <div className="text-stone-600">
                Gesamt verfügbar: <span className="font-semibold">
                  {fmtNum(Number(kontingentForm.jahreskontingent || 0) + Number(kontingentForm.uebertrag_vorjahr || 0))}
                </span> Tage
                {kontingentTarget && (
                  <> · davon bereits genehmigt: <span className="font-semibold">{fmtNum(kontingentTarget.genehmigt)}</span></>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKontingentOpen(false)}>Abbrechen</Button>
            <Button onClick={speicherKontingent} disabled={kontingentSaving}
              className="bg-[#4A463D] hover:bg-[#3A362D] text-white"
              data-testid="urlaub-kontingent-save-btn">
              {kontingentSaving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Urlaubsplanung;
